import { useEffect } from "react"
import { createFileRoute, Outlet, useNavigate, useParams } from "@tanstack/react-router"
import { Shell, type ShellEvent } from "@/components/shell/Shell"
import { resolveEventId, useActiveEvent, useEvents } from "@/core/active-event"
import { guestsQueryOptions } from "@/core/people"
import { ticketsQueryOptions } from "@/core/tickets"
import { channelsQueryOptions } from "@/core/channels"
import { conversationsQueryOptions } from "@/core/conversations"
import { agendaItemsQueryOptions } from "@/core/schedule"
import type { MyEventRow } from "@/core/types"
import { useEventRealtime } from "@/lib/use-realtime"
import { useRealtimeNotifications } from "@/lib/use-realtime-notifications"
import { useInboxRealtime } from "@/lib/use-inbox-realtime"
import { useDesktopBadge } from "@/lib/use-desktop-badge"
import { onDesktopNavigate } from "@/lib/desktop"
import { requireSession } from "@/lib/auth-guard"

// Layout route for everything event-scoped. Mounts the messaging-first shell;
// child routes render into the canvas (center card). The active-event store
// resolves the URL slugs → the real event row (membership-driven admin list);
// until it loads, the shell renders with a humanized-slug fallback.
//
// The loader resolves the URL slugs → eventId and warms the CORE nav targets'
// primary list queries (guests / tickets / channels / conversations / agenda)
// so the first canvas navigation after entering an event lands on cached data
// rather than a loading flash. It is SYNCHRONOUS and returns nothing — the
// resolve + every prefetch run fully in the background (`void
// resolveEventId(...).then(...)`), so a slow or failing events-load can never
// gate navigation into the event. Each prefetch swallows its own rejection so a
// failing surface can't reject anything. Nothing consumes a loader return here:
// the leaf routes call `resolveEventId` directly and the component reads the
// active-event store, so there's no value to hand down.
export const Route = createFileRoute("/$orgSlug/$eventSlug")({
  // Resolve the session server-side (the guard fetches get-session with the
  // forwarded cookie on SSR — the decoupled equivalent of auth.api.getSession,
  // since the BA instance lives in the attend-api worker, not here). Return the
  // user into route context AND seed ["session","me"] so the avatar/Profile —
  // which read the session via react-query — render in the SSR HTML and hydrate
  // without a flash, instead of fetching after mount.
  beforeLoad: async ({ location, context }) => {
    const user = await requireSession(location.href)
    context.queryClient.setQueryData(["session", "me"], user)
    return { user }
  },
  loader: ({ params, context }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      const { queryClient } = context
      void Promise.all([
        queryClient.ensureQueryData(guestsQueryOptions(eventId)),
        queryClient.ensureQueryData(ticketsQueryOptions(eventId)),
        queryClient.ensureQueryData(channelsQueryOptions(eventId)),
        queryClient.ensureQueryData(conversationsQueryOptions(eventId)),
        queryClient.ensureQueryData(agendaItemsQueryOptions(eventId)),
      ].map((p) => p.catch(() => undefined)))
    })
  },
  component: EventLayout,
})

function EventLayout() {
  const params = useParams({ from: "/$orgSlug/$eventSlug" })
  const navigate = useNavigate()
  const { row } = useActiveEvent()
  const loaded = useEvents((s) => s.loaded)
  const loading = useEvents((s) => s.loading)
  const load = useEvents((s) => s.load)

  // Kick the admin-events load (needs the BA session cookie — client only).
  useEffect(() => {
    if (!loaded && !loading) void load()
  }, [loaded, loading, load])

  // Realtime lives at the layout so it survives canvas navigation (and the
  // left-rail ticket list updates live on any view).
  useEventRealtime(row?.eventId)
  // Slack-style toasts (new DMs / @mentions / urgent channel posts / ticket
  // replies + assignments) + keeps conversation unread/order fresh.
  useRealtimeNotifications(row?.eventId, params)
  // Completeness net: consume the `chat.inbox.changed` nudge so inbox/thread
  // surfaces refresh on mutations that emit ONLY that signal (ticket open/close/
  // reopen, internal comments, cross-device conversation reads).
  useInboxRealtime(row?.eventId)
  // Desktop shell only (no-op in a browser): drive the native dock/taskbar
  // unread badge from the live inbox caches.
  useDesktopBadge(row?.eventId)

  // Desktop shell only: when the user clicks a native notification, the shell
  // forwards its deep-link here so we route to the thread (and not just focus
  // the window).
  useEffect(() => {
    return onDesktopNavigate((url) => {
      // `url` is a runtime deep-link string; TanStack's `to` is a typed path
      // union, so assert through the navigate fn's own param type.
      if (url.startsWith("/")) void navigate({ to: url } as Parameters<typeof navigate>[0])
    })
  }, [navigate])

  const event = toShellEvent(row, params.eventSlug)

  return (
    <Shell params={params} event={event} eventId={row?.eventId}>
      <Outlet />
    </Shell>
  )
}

function toShellEvent(row: MyEventRow | undefined, eventSlug: string): ShellEvent {
  const name = row?.name ?? humanizeSlug(eventSlug)
  return {
    name,
    dateLine: dateLine(row),
    heroImageUrl: row?.coverImageUrl ?? null,
    initials: initialsOf(name),
    accentHex: row?.primaryColor ?? null,
    themeColumn: null, // full theme_override projection lands in Phase 2
  }
}

function humanizeSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ")
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Local-time-only convention: naive date strings keyed to the event location.
// Render a compact "Mar 4–7 · Lisbon" line.
function dateLine(row: MyEventRow | undefined): string {
  if (!row) return ""
  const place = row.addressLocality ?? row.venueName ?? null
  const range = dateRange(row.startsAt, row.endsAt)
  return [range, place].filter(Boolean).join(" · ")
}

function dateRange(startsAt: string | null, endsAt: string | null): string {
  const s = parseNaive(startsAt)
  if (!s) return ""
  const e = parseNaive(endsAt)
  if (!e || (e.m === s.m && e.d === s.d)) return `${MONTHS[s.m]} ${s.d}`
  if (e.m === s.m) return `${MONTHS[s.m]} ${s.d}–${e.d}`
  return `${MONTHS[s.m]} ${s.d} – ${MONTHS[e.m]} ${e.d}`
}

function parseNaive(v: string | null): { m: number; d: number } | null {
  if (!v) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!match) return null
  const m = Number(match[2]) - 1
  const d = Number(match[3])
  if (m < 0 || m > 11) return null
  return { m, d }
}
